import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { trackPageView } from "@/lib/analytics";
import { AuthProvider } from "@/hooks/useAuth";
import { AppProvider } from "@/hooks/useApp";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import RouteLoader from "@/components/RouteLoader";

// Defer non-critical UI (toasters + Radix tooltip provider) so they don't ship
// in the initial chunk. They render nothing on first paint anyway.
const DeferredOverlays = lazy(() => import("@/components/DeferredOverlays"));

// Lazy-loaded routes to reduce initial JS bundle and defer third-party scripts (e.g. Stripe).
const Products = lazy(() => import("@/pages/Products"));
const Services = lazy(() => import("@/pages/Services"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Auth = lazy(() => import("@/pages/Auth"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const MyOrders = lazy(() => import("@/pages/MyOrders"));
const Wallet = lazy(() => import("@/pages/Wallet"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const Support = lazy(() => import("@/pages/Support"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminStock = lazy(() => import("@/pages/admin/AdminStock"));
const AdminSales = lazy(() => import("@/pages/admin/AdminSales"));
const AdminWallets = lazy(() => import("@/pages/admin/AdminWallets"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminPix = lazy(() => import("@/pages/admin/AdminPix"));
const AdminSupport = lazy(() => import("@/pages/admin/AdminSupport"));
const AdminBulkFollows = lazy(() => import("@/pages/admin/AdminBulkFollows"));
const AdminBlog = lazy(() => import("@/pages/admin/AdminBlog"));
const Tutorials = lazy(() => import("@/pages/Blog"));
const TutorialPost = lazy(() => import("@/pages/BlogPost"));

const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const MyServices = lazy(() => import("@/pages/MyServices"));
const About = lazy(() => import("@/pages/About"));
const Policies = lazy(() => import("@/pages/Policies"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

function DeferredMount({ children }: { children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const w: any = window;
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(() => setShow(true), { timeout: 3000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(id);
  }, []);
  if (!show) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AppProvider>
      <BrowserRouter>
        <AuthProvider>
          <PageViewTracker />
          <Suspense fallback={<RouteLoader />}>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/produtos" element={<Products />} />
                <Route path="/produto/:id" element={<ProductDetail />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/meus-pedidos" element={<MyOrders />} />
                <Route path="/carteira" element={<Wallet />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/suporte" element={<Support />} />
                <Route path="/servicos" element={<Services />} />
                <Route path="/servicos/:id" element={<ServiceDetail />} />
                <Route path="/meus-servicos" element={<MyServices />} />
                <Route path="/sobre" element={<About />} />
                <Route path="/politicas" element={<Policies />} />
                <Route path="/tutoriais" element={<Tutorials />} />
                <Route path="/tutoriais/:slug" element={<TutorialPost />} />
                <Route path="/blog" element={<Navigate to="/tutoriais" replace />} />
                <Route path="/blog/:slug" element={<TutorialPost />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminOverview />} />
                  <Route path="produtos" element={<AdminProducts />} />
                  <Route path="estoque" element={<AdminStock />} />
                  <Route path="vendas" element={<AdminSales />} />
                  <Route path="carteiras" element={<AdminWallets />} />
                  <Route path="pix" element={<AdminPix />} />
                  <Route path="avaliacoes" element={<AdminReviews />} />
                  <Route path="suporte" element={<AdminSupport />} />
                  <Route path="bulkfollows" element={<AdminBulkFollows />} />
                  <Route path="blog" element={<AdminBlog />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
          <DeferredMount><DeferredOverlays /></DeferredMount>
        </AuthProvider>
      </BrowserRouter>
    </AppProvider>
  </QueryClientProvider>
);

export default App;
